import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    if (this.hasAttribute('data-image-variant-sync')) {
      this.addEventListener('slideshow:select', this.#selectImageVariant, { signal });
      this.addEventListener('click', this.#selectGridImageVariant, { signal });
    }
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #selectImageVariant = (event) => {
    // Ignore automatic gallery positioning after a variant update.
    if (!event.detail.userInitiated) return;
    const media = event.detail.slide?.querySelector('[data-media-id]');
    this.#activateImageVariant(media?.dataset.mediaId);
  };

  #selectGridImageVariant = (event) => {
    const item = event.target.closest('.media-gallery__grid > li');
    if (!item) return;
    this.#activateImageVariant(item.querySelector('[data-media-id]')?.dataset.mediaId);
  };

  #activateImageVariant(mediaId) {
    if (!mediaId) return;
    const variants = JSON.parse(this.querySelector('[data-image-variants]')?.textContent || '[]');
    const variant = variants.find((item) => item.mediaId === mediaId);
    if (!variant) return;
    const picker = this.closest('.shopify-section')?.querySelector('variant-picker');
    if (!picker) return;
    const input = Array.from(picker.querySelectorAll('input[type="radio"], select option')).find(
      (option) => option.value === variant.value
    );
    if (!input) return;
    if (input instanceof HTMLOptionElement) {
      if (input.selected) return;
      input.selected = true;
      input.parentElement.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      if (input.checked) return;
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
